import React, { useState } from 'react';
import { MapPin, Clock, ShoppingCart, Star, CheckCircle, Truck, Heart, Plus } from 'lucide-react';
import { Button, Card, StatusBadge } from '../ui';
import { cn } from '../../utils/cn';
import { getPropImagePath, handleImageError } from '../../utils/imageUtils';

const PropCard = ({ 
  prop, 
  onAddToCart, 
  onRemoveFromCart, 
  onViewDetails, 
  onRequestTransfer,
  userLists = [],
  onAddToList
}) => {
  const [showListModal, setShowListModal] = useState(false);
  const [addedToLists, setAddedToLists] = useState(new Set());

  const handleTransferClick = () => {
    onRequestTransfer(prop);
  };

  const handleAddToList = (listId) => {
    if (onAddToList) {
      onAddToList(prop.id, listId);
      // Mark this list as having the prop added for visual feedback
      setAddedToLists(prev => new Set([...prev, listId]));
      // Don't automatically close the modal - let users add to multiple lists
    }
  };

  return (
    <>
      <Card className="flex flex-col sm:flex-row gap-4 p-4">
        <div className="flex-shrink-0">
          <img
            src={getPropImagePath(prop)}
            alt={prop.name}
            className="w-full sm:w-32 h-32 sm:h-24 object-cover rounded"
            onError={(e) => handleImageError(e, prop.category)}
          />
        </div>
        
        <div className="flex-1 min-w-0"> {/* min-w-0 prevents flex overflow */}
          {/* Header */}
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-semibold text-lg text-gray-900 truncate pr-2">{prop.name}</h3>
            <div className="flex gap-1 flex-shrink-0">
              {prop.retention_agreement && <Star className="h-4 w-4 text-yellow-500" />}
              {prop.inCart && <ShoppingCart className="h-4 w-4 text-blue-500" />}
            </div>
          </div>
          
          {/* Location and Owner */}
          <div className="flex flex-col sm:flex-row sm:gap-4 text-sm text-gray-600 mb-2">
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{prop.location} ({Math.round(prop.distance)} mi)</span>
            </span>
            <span className="truncate sm:border-l sm:pl-4">{prop.owner}</span>
          </div>
          
          {/* Status */}
          <div className="flex flex-wrap gap-2 mb-2">
            <StatusBadge status={prop.status} />
            <span className="text-gray-500 text-sm">• {prop.checkout_count || 0} uses</span>
          </div>
            
          {/* Tags */}
          <div className="flex flex-wrap gap-1 mb-3">
            {prop.tags?.slice(0, 4).map(tag => (
              <span key={tag} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>
          
          {/* Actions - Responsive button layout */}
          <div className="flex flex-wrap gap-2">
            {/* Primary action button */}
            {prop.status === 'available' && !prop.inCart && (
              <Button
                variant="primary"
                onClick={() => onAddToCart(prop.id)}
              >
                Add to Cart
              </Button>
            )}
            {prop.status === 'reserved' && (
              <Button
                variant="secondary"
                disabled
              >
                Reserved
              </Button>
            )}
            {prop.inCart && (
              <Button
                variant="danger"
                onClick={() => onRemoveFromCart(prop.id)}
              >
                Remove from Cart
              </Button>
            )}
            
            {/* Secondary actions */}
            <Button
              variant="secondary"
              onClick={() => onViewDetails(prop)}
            >
              Details
            </Button>
            
            {userLists.length > 0 && (
              <Button
                variant="warning"
                icon={Heart}
                onClick={() => setShowListModal(true)}
              >
                <span className="hidden sm:inline">Add to List</span>
                <span className="sm:hidden">List</span>
              </Button>
            )}
            
            {prop.transfer_available && prop.distance > 0 && (
              <Button
                variant="success"
                icon={Truck}
                onClick={handleTransferClick}
              >
                <span className="hidden sm:inline">Transfer ${prop.transfer_cost || 45}</span>
                <span className="sm:hidden">${prop.transfer_cost || 45}</span>
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Add to List Modal */}
      {showListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-semibold text-lg mb-4">Add "{prop.name}" to List</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {userLists.map(list => {
                const isAlreadyInList = list.propIds?.includes(prop.id);
                const wasJustAdded = addedToLists.has(list.id);
                
                return (
                  <div key={list.id} className="flex justify-between items-center p-3 border rounded hover:bg-gray-50">
                    <div>
                      <div className="font-medium">{list.name}</div>
                      <div className="text-sm text-gray-600">{list.propIds?.length || 0} props</div>
                      {isAlreadyInList && (
                        <div className="text-xs text-blue-600">Already in this list</div>
                      )}
                    </div>
                    <Button
                      onClick={() => handleAddToList(list.id)}
                      disabled={isAlreadyInList}
                      variant={wasJustAdded ? 'success' : isAlreadyInList ? 'secondary' : 'primary'}
                      icon={Plus}
                    >
                      {wasJustAdded ? 'Added!' : isAlreadyInList ? 'In List' : 'Add'}
                    </Button>
                  </div>
                );
              })}
            </div>
            {userLists.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Heart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No lists created yet</p>
                <p className="text-sm mt-2">Create a list first to add props to it</p>
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowListModal(false);
                  setAddedToLists(new Set()); // Reset the added state when closing
                }}
                className="flex-1"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  };

export default PropCard;